import { FC, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Box, Divider, Grid, Link, Stack, TextField, Typography } from '@mui/material';
import { SUBSCRIPTION_PLAN, SUBSCRIPTION_PLAN_2 } from 'constants/paths';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useBreakpoints } from 'hooks';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { AuthModal } from 'interfaces/auth';

import { Button } from 'components/_basic/Button/Button';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { FormField } from 'components/_basic/Form/FormField';
import { PasswordTextField } from 'components/_basic/PasswordTextField/PasswordTextField';
import { AuthModalView } from 'components/AuthModals';
import { useAuthModalsContext } from 'components/AuthModals';
import { EmailValidation } from 'components/Login/EmailValidation';
import { SocialButtons } from 'components/SocialButtons/SocialButtons';

import { signInSchema } from 'helpers/validations/signFormsValidation';

import styles from '../../Login.module.scss';
import { TFormConfig } from '../../types';

export interface ILoginFormViewProps {
  isModalOpened: boolean;
  onModalClose(): void;
  onSetUpPassword(email: string): void;
  setIsSetUpPassword(v: boolean): void;
  onForgetPasswordClick(email: string): void;
  onSignUpClick: VoidFunction;
  alert: ReactNode;
  formInitialValues: TFormConfig['initialValues'];
  onSubmit: TFormConfig['onSubmit'];
  isSetUpPassword: boolean;
  isPending: boolean;
}

const SETUP: string = 'set up';

export const LoginFormView: FC<ILoginFormViewProps> = (props) => {
  const {
    isModalOpened,
    onModalClose,
    alert,
    formInitialValues,
    onSubmit,
    isSetUpPassword,
    onSetUpPassword,
    onForgetPasswordClick,
    setIsSetUpPassword,
    isPending,
  } = props;
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();
  const { setAuthModal } = useAuthModalsContext();
  const { isAuthenticated } = useAuthenticated();
  const location = useLocation();
  const isSubscriptionPage = location.pathname === SUBSCRIPTION_PLAN;
  const isOnlyPayedSubscriptionPage = location.pathname === SUBSCRIPTION_PLAN_2;

  const closeFunction = !isAuthenticated && !isSubscriptionPage ? () => null : onModalClose;

  return (
    <AuthModalView isModalOpened={isModalOpened} onModalClose={closeFunction}>
      <Box className={styles.loginWrapper}>
        <Grid container alignItems="center" mb={{ xs: 5, sm: 3 }}>
          <Grid item xs={11}>
            <Typography variant="h1" className={styles.loginFormTitle}>
              {t('logIn')}
            </Typography>
          </Grid>
          {(isAuthenticated || isSubscriptionPage || isOnlyPayedSubscriptionPage) && (
            <Grid item xs={1}>
              <CloseButton onClick={onModalClose} />
            </Grid>
          )}
        </Grid>
        {alert}
        <Formik initialValues={formInitialValues} onSubmit={onSubmit} validationSchema={signInSchema}>
          {({ isValid, values }) => (
            <Stack spacing={3} component={Form}>
              <Field name="email">
                {({ field, meta }: FieldProps<string>) => (
                  <FormField id={'login.email'} label={t('Email')} error={meta.touched && meta.error}>
                    <TextField {...field} placeholder={t('enterYourEmail')} />
                  </FormField>
                )}
              </Field>
              <Box>
                <Field name="password">
                  {({ field, meta }: FieldProps<string>) => (
                    <FormField
                      id={'login.password'}
                      label={t('password')}
                      error={(meta.touched && meta.error) || isSetUpPassword}
                    >
                      <PasswordTextField
                        {...field}
                        autoComplete="on"
                        placeholder={t('enterYourPassword')}
                        inputProps={{ maxLength: 16 }}
                      />
                    </FormField>
                  )}
                </Field>
                {isSetUpPassword && (
                  <Box component="p" className={styles.setUpPasswordWrapper}>
                    <Trans i18nKey="youDontHaveAnAccount__plural" setup={SETUP}>
                      Your account does not have a password. Please{' '}
                      <Button
                        variant="text"
                        onClick={() => onSetUpPassword(values.email)}
                        className={styles.setUpPasswordButton}
                      >
                        {SETUP}
                      </Button>{' '}
                      a password or log in with social provider
                    </Trans>
                  </Box>
                )}
              </Box>
              <Button
                variant="text"
                className={styles.forgetPasswordButton}
                onClick={() => onForgetPasswordClick(values.email)}
              >
                {t('forgetPassword')}
              </Button>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                isLoading={isPending}
                disabled={Boolean(!isValid) || isPending}
                size={isMobile ? 'large' : 'medium'}
              >
                {t('logIn')}
              </Button>
              <EmailValidation setIsSetUpPassword={setIsSetUpPassword} />
            </Stack>
          )}
        </Formik>
        <Divider className={styles.divider}> {t('or')}</Divider>
        <SocialButtons googleTitle={t('logInWithGoogle')} facebookTitle={t('logInWithFacebook')} />
      </Box>
      <Box>
        <div className={styles.signUpDescriptionContainer}>
          <Typography variant="body2" display="inline">
            {t('dontHaveAnAccount')}
          </Typography>
          <Link className={styles.signUpLink} href="#" onClick={() => setAuthModal(AuthModal.SIGN_UP)}>
            {t('signUpForGrowexa')}
          </Link>
        </div>
        <Divider />
        <Typography display="inline" variant="body2">
          {t('singingUpAgreement')}{' '}
          <Link href="Privacy-Policy.pdf" target="_blank" rel="noopener noreferrer">
            {t('growexaPrivacyPolicy')}
          </Link>{' '}
          {t('and')}{' '}
          <Link href="Terms-of-Service.pdf" target="_blank" rel="noopener noreferrer">
            {t('termsAndConditions')}
          </Link>
        </Typography>
      </Box>
    </AuthModalView>
  );
};
